import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronUpDownIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  renderOption,
  renderSelected,
  filterFn,
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const selectedOption = useMemo(
    () => options.find(opt => String(opt.id) === String(value)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const terms = search.toLowerCase().trim().split(/\s+/);
    return options.filter(opt => {
      if (filterFn) return filterFn(opt, search);
      const text = (renderOption ? renderOption(opt) : String(opt.id)).toLowerCase();
      return terms.every(term => text.includes(term));
    });
  }, [options, search, filterFn, renderOption]);

  const handleSelect = (opt) => {
    onChange(opt);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearch('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2 border rounded-lg text-left transition-colors ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed border-gray-200 dark:border-gray-700'
            : isOpen
              ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800 bg-white dark:bg-gray-700'
              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <span className={`truncate ${selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
          {selectedOption
            ? (renderSelected ? renderSelected(selectedOption) : renderOption ? renderOption(selectedOption) : selectedOption.id)
            : placeholder}
        </span>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          {selectedOption && !disabled && (
            <XMarkIcon
              className="w-4 h-4 text-gray-400 hover:text-red-500 cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronUpDownIcon className="w-5 h-5 text-gray-400" />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-72 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-600">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          {/* Options list */}
          <ul className="overflow-y-auto max-h-52">
            {filteredOptions.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                Nenhum resultado encontrado
              </li>
            ) : (
              filteredOptions.map((opt) => (
                <li
                  key={opt.id}
                  onClick={() => handleSelect(opt)}
                  className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                    String(opt.id) === String(value)
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {renderOption ? renderOption(opt) : String(opt.id)}
                </li>
              ))
            )}
          </ul>

          {/* Counter */}
          {options.length > 5 && (
            <div className="px-3 py-1.5 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-400 dark:text-gray-500">
              {filteredOptions.length} de {options.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
